import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { setDeviceRole } from "@/lib/deviceRole";

const AuthCtx = createContext(null);

/**
 * Cookie-only auth context (iter 36+).
 *
 * No tokens live in JS-accessible storage. The HttpOnly access/refresh
 * cookies are managed by the browser; we simply call `/auth/me` on mount
 * and let the api.js interceptor silently refresh on 401.
 *
 * Legacy `vocalis_token` localStorage is wiped on first mount so users
 * upgrading from the previous build don't accidentally keep stale tokens.
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = unauth, object = auth
  const [loading, setLoading] = useState(true);

  // One-time cleanup: remove any legacy localStorage tokens left over from the
  // pre-iter-36 (Bearer) builds and pre-rebrand (Vocalis-era) keys.
  useEffect(() => {
    try {
      ["vocalis_token", "vocalis_owner_token", "vocalis_impersonating",
       "hbf_token", "hbf_owner_token"].forEach((k) =>
        localStorage.removeItem(k)
      );
    } catch (_) { /* ignore */ }
  }, []);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      // Keep the device-role marker in sync with the actual identity of the
      // last successful auth on this device.
      if (data?.role) setDeviceRole(data.role);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = async (email, password) => {
    try {
      // Bootstrap the CSRF cookie BEFORE the first unsafe POST.
      await api.get("/auth/csrf");
      const { data } = await api.post("/auth/login", { email, password });
      // Email OTP gate — do NOT set the user yet; return the challenge.
      if (data?.email_otp_challenge) {
        return {
          ok: true, email_otp_challenge: true,
          challenge_token: data.challenge_token,
          role: data.role, email_masked: data.email_masked,
          expires_in: data.expires_in,
          resend_available_in: data.resend_available_in,
          attempts_remaining: data.attempts_remaining,
          otp_dev_preview: data.otp_dev_preview,
          dev_preview_reason: data.dev_preview_reason,
        };
      }
      setUser(data.user);
      if (data.user?.role) setDeviceRole(data.user.role);
      // The /auth/login response is intentionally lean — it does NOT include
      // computed fields like `has_own_seat` (which requires a virtual_numbers
      // lookup). Immediately refresh via /auth/me so downstream gates (Seat-
      // LockedGate, Dialer, Numbers) see the full user shape without needing
      // a page refresh. Without this the agent sees a locked-out UI on their
      // FIRST landing and has to reload to unlock — the exact regression the
      // user flagged.
      try { await fetchMe(); } catch (_) { /* fall back to lean user */ }
      try { window.dispatchEvent(new Event("hbf:auth-change")); } catch (_) { /* noop */ }
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  const otpVerify = async (challenge_token, code, { trust_device = false } = {}) => {
    try {
      const { data } = await api.post("/auth/otp/verify",
        { challenge_token, code, trust_device });
      setUser(data.user);
      if (data.user?.role) setDeviceRole(data.user.role);
      // Same rationale as `login()` above — refresh via /auth/me to hydrate
      // computed fields (`has_own_seat`, `permissions`, etc.).
      try { await fetchMe(); } catch (_) { /* fall back to lean user */ }
      try { window.dispatchEvent(new Event("hbf:auth-change")); } catch (_) { /* noop */ }
      return { ok: true, trusted_device_saved: data.trusted_device_saved };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  const otpResend = async (challenge_token) => {
    try {
      const { data } = await api.post("/auth/otp/resend", { challenge_token });
      return { ok: true, ...data };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  const register = async (email, password, name) => {
    try {
      await api.get("/auth/csrf");
      const { data } = await api.post("/auth/register", { email, password, name });
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* 401 acceptable */ }
    setUser(false);
    try { window.dispatchEvent(new Event("hbf:auth-change")); } catch (_) { /* noop */ }
  };

  return (
    <AuthCtx.Provider value={{ user, loading, login, register, logout, refresh: fetchMe,
                                otpVerify, otpResend }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
